import { useFormContext, useFieldArray, useController, get as getFromErrors, type RegisterOptions } from 'react-hook-form';
import { createContext, ReactNode, useContext, useId, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { FieldTooltip } from '../FieldTooltip';
import type { FieldTooltipEntry } from '../../lib/fieldTooltips';
import {
  isoToDisplayDate,
  isoToDisplayPartial,
  displayToIsoDate,
  displayToIsoPartial,
} from '../../lib/schema-renderer/validation/dateDisplay';

/**
 * Preview mode: when true, ConditionalField renders its children unconditionally.
 * Used by the admin forms-review catalogue so reviewers can see every possible
 * branch of a form at once without filling fields.
 */
export const PreviewModeContext = createContext<{ showAll: boolean }>({ showAll: false });
export function PreviewModeProvider({ showAll, children }: { showAll: boolean; children: ReactNode }) {
  return <PreviewModeContext.Provider value={{ showAll }}>{children}</PreviewModeContext.Provider>;
}

interface BaseProps {
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  tooltip?: FieldTooltipEntry;
}

/**
 * Pull a friendly error message for a field from react-hook-form formState.
 * Returns null if no error or formState isn't wired (admin preview / catalogue).
 */
function useFieldError(name: string): string | null {
  const ctx = useFormContext();
  if (!ctx) return null;
  const err = getFromErrors(ctx.formState.errors, name);
  if (!err) return null;
  if (typeof err.message === 'string' && err.message) return err.message;
  if (err.type === 'required') return 'This field is required.';
  return 'Please check this field.';
}

function FieldLabel({
  htmlFor,
  label,
  required,
  tooltip,
}: {
  htmlFor?: string;
  label: string;
  required?: boolean;
  tooltip?: FieldTooltipEntry;
}) {
  return (
    <label className="form-label" htmlFor={htmlFor}>
      {label}
      {required ? (
        <>
          <span aria-hidden="true" className="text-[#FB8E28] ml-0.5">*</span>
          <span className="sr-only"> (required)</span>
        </>
      ) : (
        <span className="form-label-optional">(optional)</span>
      )}
      {tooltip && (
        <FieldTooltip
          description={tooltip.description}
          examples={tooltip.examples}
          ariaLabel={`Help for ${label}`}
        />
      )}
    </label>
  );
}

function FieldError({ id, message }: { id: string; message: string | null }) {
  if (!message) return null;
  return (
    <p id={id} className="form-error" role="alert">
      {message}
    </p>
  );
}

function FieldHint({ id, hint }: { id: string; hint?: string }) {
  if (!hint) return null;
  return <p id={id} className="form-hint">{hint}</p>;
}

/**
 * RC-4 (820 sweep D-07/D-15): turn the boolean `required` prop into a
 * react-hook-form rule that carries a real message ("<label> is required")
 * so no field falls back to the generic "This field is required." A caller
 * that passes explicit `rules` (the enum/text primitives pass
 * buildValidationRules(field), whose message uses the field's default label to
 * match the submit-gate summary in validateForm) still wins via the spread at
 * each call site. Non-required stays exactly `false`, so requiredness is
 * unchanged, only the message text is added.
 */
function requiredRule(required: boolean | undefined, label: string): RegisterOptions['required'] {
  return required ? `${label} is required` : false;
}

export function TextField({ name, label, hint, required, tooltip, type = 'text', placeholder, rules, transformOnBlur }: BaseProps & { type?: string; placeholder?: string; rules?: RegisterOptions; transformOnBlur?: (value: string) => string }) {
  const { register, setValue } = useFormContext();
  const reactId = useId();
  const inputId = `${reactId}-input`;
  const hintId = `${reactId}-hint`;
  const errorId = `${reactId}-error`;
  const error = useFieldError(name);
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  const registered = register(name, { required: requiredRule(required, label), ...rules });
  return (
    <div>
      <FieldLabel htmlFor={inputId} label={label} required={required} tooltip={tooltip} />
      <input
        id={inputId}
        {...registered}
        onBlur={(e) => {
          // Normalise the value on blur (e.g. uppercase a passport number)
          // before RHF's own onBlur runs validation on the normalised value.
          if (transformOnBlur) {
            const next = transformOnBlur(e.target.value);
            if (next !== e.target.value) {
              setValue(name, next, { shouldValidate: true, shouldDirty: true });
            }
          }
          void registered.onBlur(e);
        }}
        type={type}
        placeholder={placeholder}
        className="form-input"
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
      />
      <FieldHint id={hintId} hint={hint} />
      <FieldError id={errorId} message={error} />
    </div>
  );
}

/**
 * Australian-format date input. Shows and accepts DD/MM/YYYY (full dates) or
 * MM/YYYY (partial dates) while the react-hook-form value - and therefore
 * storage, validation, cross-field logic and the PDF - stays ISO. Unparseable
 * text is passed through raw so the calendar validation flags it. ISO typed
 * or pasted directly (old habits, automation) is accepted and reformatted.
 */
export function DateTextField({ name, label, hint, required, tooltip, placeholder, rules, partial = false }: BaseProps & { placeholder?: string; rules?: RegisterOptions; partial?: boolean }) {
  const reactId = useId();
  const inputId = `${reactId}-input`;
  const hintId = `${reactId}-hint`;
  const errorId = `${reactId}-error`;
  const error = useFieldError(name);
  const { field } = useController({ name, rules: { required: requiredRule(required, label), ...rules } });
  const [editing, setEditing] = useState<string | null>(null);
  const toDisplay = partial ? isoToDisplayPartial : isoToDisplayDate;
  const toIso = partial ? displayToIsoPartial : displayToIsoDate;
  const shown = editing ?? toDisplay((field.value as string | null | undefined) ?? '');
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  return (
    <div>
      <FieldLabel htmlFor={inputId} label={label} required={required} tooltip={tooltip} />
      <input
        id={inputId}
        name={field.name}
        ref={field.ref}
        value={shown}
        onFocus={() => setEditing(toDisplay((field.value as string | null | undefined) ?? ''))}
        onChange={(e) => setEditing(e.target.value)}
        onBlur={(e) => {
          const iso = toIso(e.target.value);
          field.onChange(iso ?? e.target.value.trim());
          setEditing(null);
          field.onBlur();
        }}
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        className="form-input"
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
      />
      <FieldHint id={hintId} hint={hint} />
      <FieldError id={errorId} message={error} />
    </div>
  );
}

export function TextArea({ name, label, hint, required, tooltip, placeholder, rows = 4, rules }: BaseProps & { placeholder?: string; rows?: number; rules?: RegisterOptions }) {
  const { register } = useFormContext();
  const reactId = useId();
  const inputId = `${reactId}-textarea`;
  const hintId = `${reactId}-hint`;
  const errorId = `${reactId}-error`;
  const error = useFieldError(name);
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  return (
    <div>
      <FieldLabel htmlFor={inputId} label={label} required={required} tooltip={tooltip} />
      <textarea
        id={inputId}
        {...register(name, { required: requiredRule(required, label), ...rules })}
        placeholder={placeholder}
        rows={rows}
        className="form-textarea"
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
      />
      <FieldHint id={hintId} hint={hint} />
      <FieldError id={errorId} message={error} />
    </div>
  );
}

export function Select({ name, label, options, hint, required, tooltip, rules }: BaseProps & { options: string[] | { value: string; label: string }[]; rules?: RegisterOptions }) {
  const { register } = useFormContext();
  const reactId = useId();
  const inputId = `${reactId}-select`;
  const hintId = `${reactId}-hint`;
  const errorId = `${reactId}-error`;
  const error = useFieldError(name);
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  const normalised = options.map((o) => typeof o === 'string' ? { value: o, label: o } : o);
  return (
    <div>
      <FieldLabel htmlFor={inputId} label={label} required={required} tooltip={tooltip} />
      <select
        id={inputId}
        {...register(name, { required: requiredRule(required, label), ...rules })}
        className="form-select"
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
      >
        <option value="">Select...</option>
        {normalised.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <FieldHint id={hintId} hint={hint} />
      <FieldError id={errorId} message={error} />
    </div>
  );
}

export function RadioGroup({ name, label, options, hint, required, tooltip, rules }: BaseProps & { options: string[] | { value: string; label: string }[]; rules?: RegisterOptions }) {
  const { register, watch } = useFormContext();
  const current = watch(name);
  const reactId = useId();
  const groupId = `${reactId}-group`;
  const hintId = `${reactId}-hint`;
  const errorId = `${reactId}-error`;
  const error = useFieldError(name);
  // Accept either bare strings (e.g. Yes/No) or {value,label} enum options, so
  // the visible text is the human label while the submitted value stays the enum
  // value. Without this, {value,label} enums render the raw value to the client.
  const normalised = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  return (
    <div role="group" aria-labelledby={`${reactId}-label`}>
      <span id={`${reactId}-label`} className="form-label">
        {label}
        {required ? (
          <>
            <span aria-hidden="true" className="text-[#FB8E28] ml-0.5">*</span>
            <span className="sr-only"> (required)</span>
          </>
        ) : (
          <span className="form-label-optional">(optional)</span>
        )}
        {tooltip && (
          <FieldTooltip
            description={tooltip.description}
            examples={tooltip.examples}
            ariaLabel={`Help for ${label}`}
          />
        )}
      </span>
      <div className="flex flex-wrap gap-2" id={groupId}>
        {normalised.map((o) => (
          <label
            key={o.value}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-lg border cursor-pointer transition-colors text-base min-h-[2.75rem] ${
              current === o.value
                ? 'bg-[#EBF7FD] border-[#2CABEA] text-[#0B2341]'
                : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <input
              type="radio"
              {...register(name, { required: requiredRule(required, label), ...rules })}
              value={o.value}
              className="sr-only"
              aria-required={required || undefined}
              aria-invalid={error ? true : undefined}
            />
            {o.label}
          </label>
        ))}
      </div>
      <FieldHint id={hintId} hint={hint} />
      <FieldError id={errorId} message={error} />
    </div>
  );
}

export function YesNo({ name, label, hint, required, tooltip, rules }: BaseProps & { rules?: RegisterOptions }) {
  return <RadioGroup name={name} label={label} hint={hint} required={required} tooltip={tooltip} rules={rules} options={['Yes', 'No']} />;
}

export function CheckboxGroup({ name, label, options, hint, required, tooltip, rules }: BaseProps & { options: Array<string | { value: string; label: string }>; rules?: RegisterOptions }) {
  const { register, watch } = useFormContext();
  // Accept bare strings or {value,label} pairs, exactly as Select/RadioGroup do.
  // The checkbox stores the VALUE but must display the LABEL — rendering the raw
  // value put enum codes like "FamilyViolence" in front of clients.
  const normalised = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  // Coerce to an array: saved data (or prefill) can leave a non-array value on
  // this key, and current.includes() would throw "includes is not a function",
  // crashing the whole intake page into the error boundary ("refresh the page").
  const watched = watch(name);
  const current: string[] = Array.isArray(watched) ? watched : [];
  const reactId = useId();
  const hintId = `${reactId}-hint`;
  const errorId = `${reactId}-error`;
  const error = useFieldError(name);
  return (
    <div role="group" aria-labelledby={`${reactId}-label`}>
      <span id={`${reactId}-label`} className="form-label">
        {label}
        {required ? (
          <>
            <span aria-hidden="true" className="text-[#FB8E28] ml-0.5">*</span>
            <span className="sr-only"> (required)</span>
          </>
        ) : (
          <span className="form-label-optional">(optional)</span>
        )}
        {tooltip && (
          <FieldTooltip
            description={tooltip.description}
            examples={tooltip.examples}
            ariaLabel={`Help for ${label}`}
          />
        )}
      </span>
      <div className="flex flex-wrap gap-2">
        {normalised.map((o) => {
          const checked = current.includes(o.value);
          return (
            <label
              key={o.value}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-lg border cursor-pointer transition-colors text-base min-h-[2.75rem] ${
                checked
                  ? 'bg-[#EBF7FD] border-[#2CABEA] text-[#0B2341]'
                  : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
            >
              <input
                type="checkbox"
                {...register(name, { required: requiredRule(required, label), ...rules })}
                value={o.value}
                className="sr-only"
                aria-invalid={error ? true : undefined}
              />
              {o.label}
            </label>
          );
        })}
      </div>
      <FieldHint id={hintId} hint={hint} />
      <FieldError id={errorId} message={error} />
    </div>
  );
}

export function Field({ children }: { children: ReactNode }) {
  return <div className="space-y-1">{children}</div>;
}

export function FieldRow({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}

export function Section({
  title,
  description,
  tooltip,
  children,
}: {
  title?: string;
  description?: string;
  tooltip?: FieldTooltipEntry;
  children: ReactNode;
}) {
  return (
    <div className="space-y-4">
      {title && (
        <div>
          <h3 className="text-base font-bold text-slate-800">
            {title}
            {tooltip && (
              <FieldTooltip
                description={tooltip.description}
                examples={tooltip.examples}
                ariaLabel={`Help for ${title}`}
              />
            )}
          </h3>
          {description && <p className="text-sm text-slate-500 mt-0.5">{description}</p>}
        </div>
      )}
      <div className="space-y-4">{children}</div>
    </div>
  );
}

export function InfoBox({ children, tone = 'info' }: { children: ReactNode; tone?: 'info' | 'warning' | 'error' }) {
  const styles = tone === 'error'
    ? 'bg-red-50 border-red-200 text-red-800'
    : tone === 'warning'
    ? 'bg-[#FB8E28]/10 border-[#FB8E28]/30 text-[#8a5200]'
    : 'bg-[#EBF7FD] border-[#2CABEA]/30 text-[#0B2341]';
  return (
    <div className={`rounded-lg border px-4 py-3 text-sm ${styles}`}>
      {children}
    </div>
  );
}

/**
 * SubmitErrorSummary — shows when a form submit was blocked by validation.
 * Lists missing/invalid required fields with anchor links to scroll to each.
 *
 * Usage:
 *   const { formState: { errors, isSubmitted } } = useFormContext();
 *   <SubmitErrorSummary errors={errors} visible={isSubmitted && Object.keys(errors).length > 0} />
 */
export function SubmitErrorSummary({
  errors,
  visible,
  fieldLabels = {},
}: {
  errors: Record<string, unknown>;
  visible: boolean;
  fieldLabels?: Record<string, string>;
}) {
  if (!visible) return null;
  const flat = flattenErrors(errors);
  if (flat.length === 0) return null;
  return (
    <div
      role="alert"
      aria-live="polite"
      className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
    >
      <p className="font-semibold mb-1">
        {flat.length === 1 ? '1 field needs your attention' : `${flat.length} fields need your attention`}
      </p>
      <ul className="space-y-0.5 list-disc list-inside">
        {flat.map((path) => (
          <li key={path}>
            <a href={`#${cssEscape(path)}`} className="underline hover:no-underline">
              {fieldLabels[path] ?? path}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function flattenErrors(errors: Record<string, unknown>, prefix = ''): string[] {
  const out: string[] = [];
  for (const [key, value] of Object.entries(errors ?? {})) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && 'message' in (value as object)) {
      out.push(path);
    } else if (value && typeof value === 'object') {
      out.push(...flattenErrors(value as Record<string, unknown>, path));
    }
  }
  return out;
}

function cssEscape(s: string): string {
  return s.replace(/[^a-zA-Z0-9_-]/g, (m) => `\\${m}`);
}

export function ConditionalField({ when, is, not, children }: {
  when: string;
  is?: string | boolean;
  not?: string | boolean;
  children: ReactNode;
}) {
  const { watch } = useFormContext();
  const { showAll } = useContext(PreviewModeContext);
  const value = watch(when);
  if (!showAll) {
    if (is !== undefined && value !== is) return null;
    if (not !== undefined && value === not) return null;
  }
  // In preview-all mode we surface the gating condition so a reviewer can tell
  // *why* this branch would normally appear.
  const condLabel = showAll
    ? `Shown when ${when} ${is !== undefined ? `is "${String(is)}"` : `is not "${String(not)}"`}`
    : null;
  return (
    <div className="pl-4 border-l-2 border-[#2CABEA]/30 space-y-4">
      {condLabel && (
        <div className="text-[10px] uppercase tracking-wide font-semibold text-amber-700">
          {condLabel}
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * Repeater — a repeatable group of fields with Add/Remove controls.
 * Backs the "Add more" pattern Jane asked for (other names, addresses, travel,
 * witnesses, employment, education, etc.).
 *
 * `name` is the array field path (e.g. "personal.otherNames"). The render-prop
 * receives the per-item path prefix so inner fields namespace correctly, e.g.
 *   <Repeater name="personal.otherNames" addLabel="Add another name">
 *     {(p) => <><TextField name={`${p}.familyName`} ... /></>}
 *   </Repeater>
 *
 * Persistence: the array lives under its section object and round-trips as a
 * single JSON value via formValuesToRows / rowsToFormValues.
 */
export function Repeater({
  name,
  label,
  hint,
  addLabel = 'Add another',
  emptyLabel,
  defaultItem = {},
  children,
}: {
  name: string;
  label?: string;
  hint?: string;
  addLabel?: string;
  emptyLabel?: string;
  defaultItem?: Record<string, unknown>;
  children: (path: string, index: number) => ReactNode;
}) {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name });
  return (
    <div className="space-y-3">
      {label && <span className="form-label">{label}</span>}
      {hint && <p className="form-hint">{hint}</p>}
      {fields.length === 0 && emptyLabel && (
        <p className="text-sm text-slate-400 italic">{emptyLabel}</p>
      )}
      {fields.map((field, index) => (
        <div key={field.id} className="rounded-lg border border-slate-200 p-4 space-y-4 relative">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              #{index + 1}
            </span>
            <button
              type="button"
              onClick={() => remove(index)}
              className="text-xs text-red-600 hover:underline inline-flex items-center gap-1"
            >
              <Trash2 size={12} /> Remove
            </button>
          </div>
          {children(`${name}.${index}`, index)}
        </div>
      ))}
      <button
        type="button"
        onClick={() => append(defaultItem)}
        className="btn-secondary text-sm inline-flex items-center gap-1.5"
      >
        <Plus size={14} /> {addLabel}
      </button>
    </div>
  );
}
